import { ServerHandler } from "./ServerHandler";
import { createAiClient, type AiClient } from "../../_genApi/assistant/ai-client";
import Network from "./Network";
import Auth from "../auth/auth";

export class ServerAssistant extends ServerHandler {
	private aiServiceUrl: string | null = null;

	private aiClient: AiClient | null = null;

	private token: string = "";

	private urlPromise: Promise<string> | null = null;

	constructor() {
		super();
	}

	/**
	 * Called by auth modules (GuestAuthModule, SocialAuthModule) on login.
	 * Token is read dynamically per request via the middleware in getAiClient(),
	 * so no cache invalidation is needed.
	 */
	setToken(token: string): void {
		this.token = token;
	}

	/**
	 * Kept for backward compatibility (setO365Ticket calls in legacy code paths).
	 */
	setO365Ticket(newTicket: string): void {
		this.setToken(newTicket);
	}

	/**
	 * Discover the ai-service URL from the platform server.
	 * Caches the result — only one network call per session.
	 */
	async resolveAiServiceUrl(): Promise<string> {
		if (this.aiServiceUrl) return this.aiServiceUrl;

		if (!this.urlPromise) {
			this.urlPromise = (async () => {
				const platformUrl = Network.withServerUploadGame().getServerUrl();
				const res = await fetch(`${platformUrl}/api/url-assistant`);
				if (!res.ok) {
					throw new Error(`Failed to discover ai-service URL: ${res.status}`);
				}
				const data = await res.json();

				console.log("resolveAiServiceUrl", data);

				const url = (data.url as string).replace(/\/+$/, "");
				this.aiServiceUrl = url || process.env.REACT_APP_AI_ASSISTANT_SERVICE_URL;
				return url;
			})();
		}

		return this.urlPromise;
	}

	/**
	 * Returns the cached ai-service URL synchronously.
	 * Falls back to REACT_APP_AI_ASSISTANT_SERVICE_URL env var if not yet discovered.
	 * Used by GameModal/buildPlayerUrl() which needs a sync URL.
	 */
	getServerUrl(): string {
		if (this.aiServiceUrl) return this.aiServiceUrl;

		const isLive = process.env.REACT_APP_MODE?.trim() === "LIVE";
		return (isLive
			? process.env.REACT_APP_LIVE_PUBLIC_URL
			: process.env.REACT_APP_AI_ASSISTANT_SERVICE_URL) || "";
	}

	/**
	 * Get a typed openapi-fetch client for the ai-service.
	 * Resolves the URL on first call, then caches the client. The token is
	 * read fresh per request via middleware, so the cached client always
	 * uses the current login token.
	 */
	async getAiClient(): Promise<AiClient> {
		if (this.aiClient) return this.aiClient;

		const url = await this.resolveAiServiceUrl();
		this.aiClient = createAiClient(
			url,
			() => this.token || Auth.getLoginToken() || undefined,
		);
		return this.aiClient;
	}
}
